// Browser-history integration for the compact surfaces (KSE-0191). Each
// stacked layer (mail: mailbox drawer / open message / composer; calendar:
// event sheet / calendars sheet) maps to one pushed history entry
// (navigation.ts), so the platform back gesture — swipe from the edge on iOS,
// the Android back button, the browser's Back — pops the layer instead of
// leaving the app. The UI stays the source of truth: `useStackHistory` diffs
// what the UI shows against what history records and pushes / goes back /
// rewrites accordingly; a `popstate` (the user went back) reports the layers
// the landed-on entry still records so the caller closes the rest. Desktop
// pushes nothing. `useMailHistory` is the mail surface's binding; the
// calendar's lives in src/calendar/useCalendarHistory.ts.

import { useCallback, useEffect, useRef } from "react";

import {
  desiredLayers,
  type HistoryLayer,
  layersFromState,
  MAIL_LAYERS,
  planHistory,
  popTo,
  stateForLayers,
} from "./navigation";

export interface StackHistoryInput {
  /** The layers the UI wants recorded right now (empty when not compact/active). */
  layers: readonly HistoryLayer[];
  /** The layers this surface owns: entries of the other surface read as the root. */
  owned: readonly HistoryLayer[];
  /** Whether a user popstate should close anything (false = desktop / inactive surface). */
  enabled: boolean;
  onPop: (layers: HistoryLayer[]) => void;
}

// `history.go()` is asynchronous; if nothing comes back in this long the
// traversal is treated as dropped and the stack is reconciled from scratch.
const TRAVERSAL_TIMEOUT_MS = 1_000;

export function useStackHistory({ layers, owned, enabled, onPop }: StackHistoryInput) {
  // What history records right now (seeded from the entry the page loaded on:
  // a reload keeps the pushed entries and their state).
  const recorded = useRef<HistoryLayer[] | null>(null);
  if (recorded.current === null) {
    recorded.current = typeof history === "undefined" ? [] : layersFromState(history.state, owned);
  }
  // What the UI wants recorded (the latest render's layers).
  const desired = useRef<HistoryLayer[]>([]);
  // A `history.go(-n)` of ours is in flight: its popstate is not the user's,
  // and no further push/replace happens until it lands (or times out).
  const traversal = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onPopRef = useRef(onPop);
  onPopRef.current = onPop;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const ownedRef = useRef(owned);
  ownedRef.current = owned;

  const reconcile = useCallback(() => {
    if (typeof history === "undefined" || traversal.current !== null) {
      return;
    }
    const plan = planHistory(recorded.current ?? [], desired.current);
    switch (plan.kind) {
      case "none":
        return;
      case "push": {
        const next = [...(recorded.current ?? [])];
        for (const layer of plan.layers) {
          next.push(layer);
          history.pushState(stateForLayers(next), "");
        }
        recorded.current = next;
        return;
      }
      case "back":
        recorded.current = desired.current;
        traversal.current = setTimeout(() => {
          traversal.current = null;
          reconcile();
        }, TRAVERSAL_TIMEOUT_MS);
        history.go(-plan.count);
        return;
      case "replace":
        recorded.current = desired.current;
        history.replaceState(stateForLayers(desired.current), "");
        return;
      default:
        return;
    }
  }, []);

  // The array identity changes every render; diff by content.
  const layersKey = layers.join("/");
  useEffect(() => {
    desired.current = layersKey === "" ? [] : (layersKey.split("/") as HistoryLayer[]);
    reconcile();
  }, [layersKey, reconcile]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const onPopState = (event: PopStateEvent) => {
      const landed = layersFromState(event.state, ownedRef.current);
      recorded.current = landed;
      if (traversal.current !== null) {
        // Our own go(-n) landed: the UI already matches; apply anything the
        // user did in the meantime.
        clearTimeout(traversal.current);
        traversal.current = null;
        reconcile();
        return;
      }
      // Desktop records no layers: a popstate there (e.g. the entries left
      // over from a phone-width session) must not close anything.
      if (enabledRef.current) {
        onPopRef.current(landed);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      if (traversal.current !== null) {
        clearTimeout(traversal.current);
        traversal.current = null;
      }
    };
  }, [reconcile]);
}

export interface MailHistoryInput {
  compact: boolean;
  drawerOpen: boolean;
  messageOpen: boolean;
  composerOpen: boolean;
  onPop: (open: { composerOpen: boolean; drawerOpen: boolean; messageOpen: boolean }) => void;
}

export function useMailHistory({
  compact,
  drawerOpen,
  messageOpen,
  composerOpen,
  onPop,
}: MailHistoryInput) {
  const onPopRef = useRef(onPop);
  onPopRef.current = onPop;
  const handlePop = useCallback((layers: HistoryLayer[]) => onPopRef.current(popTo(layers)), []);
  useStackHistory({
    layers: desiredLayers({ compact, drawerOpen, messageOpen, composerOpen }),
    owned: MAIL_LAYERS,
    enabled: compact,
    onPop: handlePop,
  });
}
